import { Chart as ChartJS } from "chart.js";

// Categorical palette shared by all dashboard charts
export const CHART_COLORS = [
  "#15803d", // green (brand)
  "#2563eb", // blue
  "#f59e0b", // amber
  "#7c3aed", // violet
  "#dc2626", // red
  "#0d9488", // teal
  "#db2777", // pink
  "#65a30d", // lime
  "#475569", // slate
  "#ea580c", // orange
  "#0891b2", // cyan
];

// Year encoding is consistent across every chart:
// current year = brand green, previous year = muted gray
export const YEAR_COLORS = {
  current: "#15803d",
  previous: "#94a3b8",
};

export const REST_COLOR = "#cbd5e1";

export const colorAt = (index) => CHART_COLORS[index % CHART_COLORS.length];

export const withAlpha = (hexColor, alpha) => {
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Scriptable background for line charts: fades the line color to transparent
export const verticalGradient = (hexColor) => (context) => {
  const { ctx, chartArea } = context.chart;
  if (!chartArea) return withAlpha(hexColor, 0.15);
  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
  gradient.addColorStop(0, withAlpha(hexColor, 0.25));
  gradient.addColorStop(1, withAlpha(hexColor, 0));
  return gradient;
};

export const applyChartDefaults = () => {
  ChartJS.defaults.font.family =
    'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
  ChartJS.defaults.font.size = 12;
  ChartJS.defaults.color = "#64748b";
};

export const baseOptions = () => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: "bottom",
      labels: {
        usePointStyle: true,
        pointStyle: "circle",
        boxWidth: 8,
        boxHeight: 8,
        padding: 16,
      },
    },
    tooltip: {
      backgroundColor: "rgba(15, 23, 42, 0.92)",
      padding: 12,
      cornerRadius: 8,
      boxPadding: 4,
      usePointStyle: true,
    },
  },
});

export const monthlyScales = (yOverrides = {}) => ({
  x: {
    grid: { display: false },
    border: { display: false },
  },
  y: {
    beginAtZero: true,
    grid: { color: "rgba(100, 116, 139, 0.12)" },
    border: { display: false, dash: [4, 4] },
    ticks: { maxTicksLimit: 6, precision: 0 },
    ...yOverrides,
  },
});

// Months that haven't happened yet are unknown, not zero — null makes
// lines end at the current month instead of dropping to 0.
export const nullifyFutureMonths = (monthlyData) => {
  const currentMonth = new Date().getMonth();
  return monthlyData.map((value, monthIndex) => (monthIndex > currentMonth ? null : (value ?? 0)));
};

export const yearBarDataset = ({ label, data, isCurrentYear }) => ({
  label,
  data,
  backgroundColor: isCurrentYear ? YEAR_COLORS.current : YEAR_COLORS.previous,
  borderWidth: 0,
  borderRadius: 4,
  maxBarThickness: 16,
});

export const yearLineDataset = ({ label, data, isCurrentYear }) => ({
  label,
  data,
  borderColor: isCurrentYear ? YEAR_COLORS.current : YEAR_COLORS.previous,
  backgroundColor: isCurrentYear ? verticalGradient(YEAR_COLORS.current) : "transparent",
  fill: isCurrentYear,
  borderWidth: 2.5,
  borderDash: isCurrentYear ? [] : [6, 4],
  tension: 0.35,
  pointRadius: 0,
  pointHoverRadius: 5,
  pointBackgroundColor: isCurrentYear ? YEAR_COLORS.current : YEAR_COLORS.previous,
});
